import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'

import { useSafetyTranslation } from 'cng-common/features/translations'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../configs/translations'
import { setCockpitAttentionFullscreen } from '../../model/financialCockpitSlice'
import { CockpitFilterSummary } from '../components/cockpitFilterSummary'
import { CockpitTileTitle } from '../components/cockpitTileTitle'

import { AttentionCount } from './components/attentionCount'
import { ManagerAttentionPlanningStatus } from './components/attentionPlanningStatus'
import { AttentionStatusesGrouping } from './components/attentionStatusesGrouping'
import { AttentionStatusesSearch } from './components/attentionStatusesSearch'
import { useAttentionGrouping } from './hooks/useAttentionGrouping'
import { useAttentionScopePbmCount } from './hooks/useAttentionScopePbmCount'
import { useAttentionScopeSize } from './hooks/useAttentionScopeSize'
import { useAttentionSearch } from './hooks/useAttentionSearch'
import { PARTNER_UNIT, PBM_UNIT } from './hooks/useAttentionStatusesText'
import { useHasAttentionStatusDrill } from './hooks/useHasAttentionStatusDrill'
import { useHasAttentionStatuses } from './hooks/useHasAttentionStatuses'

import styles from './CockpitAttentionStatusesTitle.module.scss'

interface ICockpitAttentionStatusesTitleProps {
  title: string
  expanded: boolean
  onToggle: () => void
  /** Manager cockpit: statuses are counted across the partners currently shown, and the header's
   *  context line states the planning state across them. The single-partner cockpit states the
   *  selected programme's in the plan deck over the board. */
  isManagerCockpitMode?: boolean
}

/**
 * Tile header of the attention-statuses tile. `Show detailed` is dropped while collapsed — the
 * rows themselves expand the tile — but `Show summary` stays so the expanded state can be closed.
 *
 * The grouping switcher belongs to the manager cockpit's opened status: it picks how the partners
 * behind that status are listed, so on the status list itself — and in the single-partner cockpit,
 * which has one partner — there is nothing for it to do. With nothing raised it goes too, since
 * the tile then draws one stub whichever grouping is picked. The search beside it narrows the
 * level the grouping lists, so it stands with the groupings that list names. The manager tile also
 * opens over the whole screen, for the partner table.
 */
export const CockpitAttentionStatusesTitle = ({
  title,
  expanded,
  onToggle,
  isManagerCockpitMode = false,
}: ICockpitAttentionStatusesTitleProps) => {
  const t = useSafetyTranslation()
  const dispatch = useAppDispatch()
  const { grouping, options, scopeName, selectGrouping } = useAttentionGrouping()
  const { search, setSearch } = useAttentionSearch()
  const hasStatuses = useHasAttentionStatuses(isManagerCockpitMode)
  const isInsideStatus = useHasAttentionStatusDrill(isManagerCockpitMode)
  const scopeSize = useAttentionScopeSize(isManagerCockpitMode)
  const scopePbmCount = useAttentionScopePbmCount(isManagerCockpitMode)

  return (
    <CockpitTileTitle
      title={title}
      expanded={expanded}
      onToggle={onToggle}
      /* The manager tile keeps the expand control at all times: its list runs past the tile, and
         the reader opens it for room rather than only to leave a detail behind. The
         single-partner tile still expands through its own rows. */
      hideToggleWhenCollapsed={!isManagerCockpitMode}
      fullscreen={
        isManagerCockpitMode && hasStatuses
          ? {
              label: t(FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.fullscreen.open),
              onOpen: () => dispatch(setCockpitAttentionFullscreen(true)),
            }
          : undefined
      }
      actions={
        isInsideStatus && hasStatuses && grouping ? (
          <>
            {/* A PBM has one grouping, so the switcher would offer nothing. */}
            {options.length > 1 && (
              <AttentionStatusesGrouping
                grouping={grouping}
                options={options}
                scopeName={scopeName}
                onChange={selectGrouping}
              />
            )}
            {/* The geo tree is walked, not searched: its rows are markets and clusters, and the
                names the reader looks for sit levels below them. */}
            {grouping !== 'market' && (
              <AttentionStatusesSearch grouping={grouping} value={search} onChange={setSearch} />
            )}
          </>
        ) : null
      }
    >
      <CockpitFilterSummary expanded={expanded} bordered={false} offering category />
      {isManagerCockpitMode && <ManagerAttentionPlanningStatus />}
      {/* The base every row is counted against, said once for the whole list. */}
      {scopeSize > 0 && (
        <span className={styles.scope}>
          <span className={styles.separator} aria-hidden>
            &middot;
          </span>
          <AttentionCount count={scopeSize} unit={PARTNER_UNIT} />
          <span className={styles.separator} aria-hidden>
            &middot;
          </span>
          <AttentionCount count={scopePbmCount} unit={PBM_UNIT} />
        </span>
      )}
    </CockpitTileTitle>
  )
}

CockpitAttentionStatusesTitle.displayName = 'CockpitAttentionStatusesTitle'
